import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { isEmpty, keyBy, mapValues } from 'lodash-es';
import { ComponentProps, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ContentId } from 'src/utils/constants/contentId';

import { EventVenueMapFormType } from '../../EventVenueMapModal';
import { DragableSectionGroupItem } from './DragableSectionGroupItem';
import { PlaceHolderZoneGroup } from './PlaceHolderZoneGroup';
import * as styles from './SectionGroupPanelV2.css';
import { useSectionGroupOperations } from './SectionGroupPanelV2.hooks';

export const SectionGroupPanelV2 = ({ eventId }: { eventId: string }) => {
  const { watch, setValue } = useFormContext<EventVenueMapFormType>();
  const containerRef = useRef<HTMLDivElement>(null);

  const name = watch('name');
  const sectionSectionGroupLookup = watch('sectionSectionGroupLookup');
  const customSectionGroups = watch('customSectionGroups');
  const confirmationDialog = useBasicDialog();

  const { venueMapInfo } = useEventMapContext();

  const {
    groupIdToSections,
    sortabledSectionGroups,
    handleDragEnd,
    onUpdateSectionGroup,
    onDeleteSectionGroup,
    onClearAllSectionGroups,
  } = useSectionGroupOperations(sectionSectionGroupLookup, customSectionGroups);

  const groupIdToSectionInfo = useMemo(() => {
    const sectionById = keyBy((venueMapInfo ?? {}).sections, (s) => s.id);
    return mapValues(groupIdToSections, (sections) => {
      return sections.map((section) => sectionById[section.sectionId] ?? []);
    });
  }, [groupIdToSections, venueMapInfo]);

  return (
    <div className={styles.gridPanelContainer}>
      <PosTextField
        type={'text'}
        maxLength={200}
        onChange={(e) => {
          setValue('name', e.target.value);
        }}
        value={name}
        prefixDisplay={
          <span className={styles.mapTitlePrefix}>
            <Content id={ContentId.MapTitle} />:
          </span>
        }
      />
      <Stack alignItems="end" justifyContent="spaceBetween">
        <Stack direction="column">
          <span className={styles.headerText}>
            <Content id={ContentId.ZoneGroups} />
          </span>
          <span className={styles.subHeaderText}>
            <Content id={ContentId.ZoneGroupDescription} />
          </span>
        </Stack>

        <Button
          className={styles.deleteAllGroupsText}
          variant="textPlain"
          onClick={confirmationDialog.launchDialog}
        >
          <Content id={ContentId.DeleteAllGroups} />
        </Button>
      </Stack>
      <Stack
        ref={containerRef}
        direction="column"
        width="full"
        height="full"
        style={{ flex: 1, minHeight: 0 }}
      >
        {isEmpty(sortabledSectionGroups) ? (
          <PlaceHolderZoneGroup />
        ) : (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={sortabledSectionGroups}
              strategy={verticalListSortingStrategy}
            >
              <Stack
                direction="column"
                width="full"
                gap="s"
                style={{ overflowY: 'auto' }}
              >
                {sortabledSectionGroups.map(({ id, ...ssg }) => (
                  <DragableSectionGroupItem
                    key={id}
                    id={id}
                    eventId={eventId}
                    sectionGroup={ssg}
                    sections={groupIdToSectionInfo[id]}
                    existingGroupIds={sortabledSectionGroups.map(
                      ({ id }) => id
                    )}
                    onUpdateSectionGroup={onUpdateSectionGroup}
                    onRemoveSectionGroup={onDeleteSectionGroup}
                  />
                ))}
              </Stack>
            </SortableContext>
            <ClearAllSectionGroupsConfirmationDialog
              {...confirmationDialog.dialogProps}
              onConfirm={() => {
                onClearAllSectionGroups();
                confirmationDialog.closeDialog();
              }}
              onClosed={confirmationDialog.closeDialog}
            />
          </DndContext>
        )}
      </Stack>
    </div>
  );
};

export type ClearAllSectionGroupsConfirmationDialogProps = ComponentProps<
  typeof RSModal
> & {
  onConfirm: () => void;
};

const ClearAllSectionGroupsConfirmationDialog = ({
  onConfirm,
  onClosed,
  ...rest
}: ClearAllSectionGroupsConfirmationDialogProps) => {
  return (
    <GenericDialog
      {...rest}
      size="m"
      header={<Content id={ContentId.ConfirmDeletion} />}
      footer={
        <Stack width="full" justifyContent="spaceBetween">
          <Button variant={'outline'} onClick={onClosed}>
            <Content id={ContentId.Cancel} />
          </Button>
          <Button variant={'text'} onClick={onConfirm}>
            <span style={{ color: vars.color.textErrorV2 }}>
              <Content id={ContentId.Delete} />
            </span>
          </Button>
        </Stack>
      }
      onCancel={onClosed}
    >
      <Stack direction="column" gap="m">
        <span>
          <Content id={ContentId.ClearGroupsWarning} />
        </span>
        <span>
          <Content id={ContentId.ClearGroupsWarning2} />
        </span>
      </Stack>
    </GenericDialog>
  );
};
